/**
 * Modal for adding/editing possible values with better visibility
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Box,
  Typography,
} from '@mui/material';

interface PossibleValueEditModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (value: string) => void;
  initialValue?: string;
  dataType: string;
  mode: 'add' | 'edit';
}

export const AllowedValueEditModal: React.FC<PossibleValueEditModalProps> = ({
  open,
  onClose,
  onSave,
  initialValue = '',
  dataType,
  mode,
}) => {
  const [value, setValue] = useState<string>(initialValue);
  const [error, setError] = useState<string>('');

  // Reset value when modal opens
  useEffect(() => {
    if (open) {
      setValue(initialValue);
      setError('');
    }
  }, [open, initialValue]);

  // Validate input based on data type
  const validateInput = (str: string): boolean => {
    if (!str.trim()) {
      setError('Value cannot be empty');
      return false;
    }

    switch (dataType) {
      case 'BOOLEAN':
        if (str !== 'true' && str !== 'false') {
          setError('Boolean value must be "true" or "false"');
          return false;
        }
        break;
      case 'INTEGER':
        if (!/^-?\d+$/.test(str)) {
          setError('Must be a valid integer (e.g., 42, -10)');
          return false;
        }
        break;
      case 'FLOAT':
        if (!/^-?\d+\.?\d*$/.test(str)) {
          setError('Must be a valid number (e.g., 3.14, -2.5)');
          return false;
        }
        break;
      case 'JSON':
        try {
          JSON.parse(str);
        } catch {
          setError('Must be valid JSON (object or array)');
          return false;
        }
        break;
      case 'COLOR':
        // Only accept valid 3-digit (#RGB) or 6-digit (#RRGGBB) hex colors
        if (!/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(str)) {
          setError('Must be a valid hex color (e.g., #FFF or #D7D7D7)');
          return false;
        }
        break;
    }

    setError('');
    return true;
  };

  const handleSave = () => {
    if (validateInput(value)) {
      onSave(value);
      onClose();
    }
  };

  const getPlaceholder = () => {
    switch (dataType) {
      case 'BOOLEAN': return 'true or false';
      case 'INTEGER': return 'e.g., 42';
      case 'FLOAT': return 'e.g., 3.14';
      case 'JSON': return 'e.g., {"key": "value"} or ["item1", "item2"]';
      case 'DATETIME': return 'e.g., 2024-01-15T10:30:00Z';
      case 'COLOR': return 'e.g., #D7D7D7 or #FFF';
      default: return 'Enter value';
    }
  };

  const isJsonType = dataType === 'JSON';

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {mode === 'add' ? 'Add Possible Value' : 'Edit Possible Value'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Data Type: <strong>{dataType}</strong>
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={isJsonType ? 12 : 6}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError('');
            }}
            placeholder={getPlaceholder()}
            autoFocus
            error={!!error}
            sx={{
              fontFamily: isJsonType ? 'monospace' : 'inherit',
              '& .MuiInputBase-input': {
                fontSize: isJsonType ? '0.875rem' : 'inherit',
              },
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={!value.trim()}
        >
          {mode === 'add' ? 'Add' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

